import React, { useState, useEffect } from 'react';
import { X, Calendar, Headphones, ChevronRight, LogOut } from 'lucide-react';
import emailIcon from '../../assets/Icons/email.png';
import WhatsAppIcon from '../../assets/Icons/WhatsAppIcon';
import { useNavigate } from 'react-router-dom';
import { countInsurancePatientByMail } from '../../Services/APIs/patient';
import toothlensLogo from "../../assets/illustrations/toothlens-logo-tagline.png";
import ReimbursementSection from './Reimbursement/ReimbursementSection';
import { DIALOG_TITLE_ID } from './Reimbursement/FormShell';
import { clearPhonepeSession, getPhonepeCompany } from './phonepeSession';

// Layout and colours follow Phone Portal Layout.html (repo root); the `portal-*` colours are in
// tailwind.config.js.
const SectionLabel = ({ children, className = 'mt-[22px]' }) => (
  <p className={`text-xs font-bold tracking-[1px] uppercase text-portal-label mb-2.5 mx-0.5 ${className}`}>{children}</p>
);

const StepPill = ({ step, className }) => (
  <span className={`text-[10px] font-bold tracking-[0.5px] px-[9px] py-[3px] rounded-full ${className}`}>STEP {step}</span>
);

// Fork of src/Components/PlumIframeLink/iframeLink.js, isolated to the phonepe* localStorage
// namespace. Network Clinic booking navigates to the existing generic `/:company-appointment`
// route (unmodified) instead of opening PlumAppointment as an inline modal, so `company`
// correctly threads through via that route's own useParams()/URL parsing.
// Members of any company whose wallet has Reimbursement land here (also from the general login),
// so the company is the signed-in member's own, not a fixed 'phonepe'.
const PhonepeDashboard = () => {
  const navigate = useNavigate();
  const PHONEPE_COMPANY = getPhonepeCompany();
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  const [patientsList, setPatientsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSessionTimeout, setShowSessionTimeout] = useState(false);
  const [kycRequired, setKycRequired] = useState(
    localStorage.getItem('phonepeKycRequired') !== 'false'
  );
  const [reimbursementRequired] = useState(
    localStorage.getItem('phonepeReimbursementRequired') === 'true'
  );
  const [kycPendingDetails, setKycPendingDetails] = useState([]);
  const [kycBlockedReason, setKycBlockedReason] = useState('');
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  // Where the Reimbursement modal opens (null = closed): 'list', 'request' (new prescription) or
  // 'proof' (proof of treatment). `?open=reimbursement` (e.g. from "no clinic found" in the
  // booking flow) opens the list straight away.
  const [reimbursementView, setReimbursementView] = useState(
    () => (reimbursementRequired && new URLSearchParams(window.location.search).get('open') === 'reimbursement' ? 'list' : null)
  );

  // Check authentication on mount
  useEffect(() => {
    const loginEmail = localStorage.getItem('phonepeLoginEmail');
    const storedCompany = localStorage.getItem('phonepeCompany');
    if (!loginEmail || !storedCompany) {
      navigate('/phonepe/login');
      return;
    }
    // A session from before patient tokens existed cannot use Reimbursement; sign in again.
    if (reimbursementRequired && !localStorage.getItem('phonepeToken')) {
      clearPhonepeSession();
      navigate('/phonepe/login');
    }
  }, [navigate, reimbursementRequired]);

  // Auto logout after 2 hours
  useEffect(() => {
    const autoLogoutTimer = setTimeout(() => {
      setShowSessionTimeout(true);
    }, 2 * 60 * 60 * 1000); // 2 hours in milliseconds

    return () => clearTimeout(autoLogoutTimer);
  }, [navigate]);

  const handleExplorePlan = () => {
    window.open('https://toothlens.com/dental360/terms', '_blank', 'noopener,noreferrer');
  };

  // Navigate into the existing, unmodified `/:company-appointment` booking flow, prefilled
  // with the looked-up member.
  const goToBooking = ({ uid, name, email, mobile }) => {
    const params = new URLSearchParams({
      prefill: 'true',
      uid: uid || '',
      name: name || '',
      email: email || '',
      mobile: mobile || '',
    });
    navigate(`/${PHONEPE_COMPANY}-appointment?${params.toString()}`);
  };

  const handleBookAppointment = async () => {
    const storedEmail = localStorage.getItem('phonepeLoginEmail');

    if (!storedEmail) {
      setErrorMessage('No email found. Please login again.');
      navigate('/phonepe/login');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('email', storedEmail);
      formData.append('company', PHONEPE_COMPANY);

      const response = await countInsurancePatientByMail(formData);

      if (response.status === 200) {
        const data = response.data?.data || {};
        const patients = data.patients || [];
        const kycPending = data.kyc_pending || [];
        const employeeEmail = data.employee_mail || storedEmail;
        const phoneNumber = data.phone_number || localStorage.getItem('phonepePhoneNumber') || '';

        if (!kycRequired) {
          // ── kyc_required: false ──────────────────────────────────────
          if (patients.length === 0) {
            setErrorMessage('No members found for this account.');
          } else if (patients.length === 1) {
            goToBooking({
              uid: patients[0].uid,
              name: patients[0].name,
              email: employeeEmail,
              mobile: phoneNumber
            });
          } else {
            const selectable = patients.map((p) => ({
              uid: p.uid,
              name: p.name,
              email: employeeEmail,
              phone_number: phoneNumber,
              relation_to_patient: p.relationship || ''
            }));
            setPatientsList(selectable);
            setShowPatientSelection(true);
          }
        } else {
          // ── kyc_required: true ───────────────────────────────────────
          const getStatus = (review_status) => {
            const map = {
              reviewed: 'Active',
              rejected: 'Rejected',
              retake: 'Retake Requested',
              xray_request: 'X-Ray Requested',
              new: 'In Process',
              retaken: 'In Process',
            };
            return map[review_status] || 'Pending';
          };

          const combined = [
            ...patients.map((p) => ({
              uid: p.uid,
              name: p.name || p.uid,
              relation: p.relationship || '—',
              status: getStatus(p.review_status),
              canBook: p.review_status === 'reviewed',
              patientData: p.review_status === 'reviewed'
                ? { uid: p.uid, name: p.name, email: p.email, mobile: p.phone_number }
                : null
            })),
            ...kycPending.map((p) => ({
              uid: p.uid,
              name: p.name || p.uid,
              relation: p.relationship || '—',
              status: 'Pending',
              canBook: false,
              patientData: null
            }))
          ];

          if (combined.length === 0) {
            setErrorMessage('No members found for this account.');
            return;
          }

          if (combined.length === 1 && combined[0].canBook) {
            goToBooking(combined[0].patientData);
          } else {
            setKycPendingDetails(combined);
          }
        }
      }
    } catch (error) {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSelect = (patient) => {
    setShowPatientSelection(false);
    goToBooking({
      uid: patient.uid,
      name: patient.name,
      email: patient.email,
      mobile: patient.phone_number
    });
  };

  const handleClosePatientSelection = () => {
    setShowPatientSelection(false);
    setPatientsList([]);
  };

  const handleLogout = () => {
    clearPhonepeSession();
    setKycPendingDetails([]);
    setKycBlockedReason('');
    navigate('/phonepe/login');
  };

  const handleOpenKYC = () => {
    const email = localStorage.getItem('phonepeLoginEmail');

    if (email) {
      const data = btoa(JSON.stringify({ email, company: PHONEPE_COMPANY }));
      window.open(`https://kyc.toothlens.com/group/${PHONEPE_COMPANY}/front?auth=${data}`, '_blank');
    } else {
      window.open(`https://kyc.toothlens.com/group/${PHONEPE_COMPANY}/front`, '_blank');
    }
  };

  // Full-width gradient tile; step 1 in both dashboard layouts when a scan is needed.
  const scanTile = (
    <button
      onClick={handleOpenKYC}
      className="w-full flex items-center justify-between gap-3 text-left text-white bg-gradient-to-br from-portal-blue to-portal-blue-dark rounded-[18px] px-[18px] py-3 shadow-[0_8px_20px_rgba(47,95,224,0.25)] hover:shadow-[0_10px_24px_rgba(47,95,224,0.35)] transition-shadow"
    >
      <div>
        <div className="flex items-center gap-2">
          <p className="text-[15px] font-bold">AI Dental Scan</p>
          <StepPill step={1} className="bg-white/20" />
        </div>
        <p className="text-[11px] text-white/85 leading-[1.3] mt-px">Complete your scan &amp; activate your plan.</p>
      </div>
      <span className="flex-shrink-0 whitespace-nowrap bg-white text-portal-blue-dark text-xs font-bold px-[13px] py-[7px] rounded-[9px] mt-0.5">
        Start &gt;
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-portal-bg flex flex-col">

      {/* Header */}
      <div className="px-4 pt-3 flex items-center justify-end">
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-sm font-semibold text-portal-sub hover:text-red-600 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 w-full max-w-[420px] mx-auto px-3 pb-[60px] text-portal-ink">

        <img
          src={toothlensLogo}
          alt="ToothLens - Smarter Dental Care. Simplified."
          className="w-full max-w-[280px] h-auto mx-auto mt-2 mix-blend-multiply"
        />

        {/* Tagline */}
        <h2 className="text-center text-[19px] font-bold text-portal-blue mt-3.5 mb-[22px]">
          One Plan. Total Protection
        </h2>

        {/* YOUR PLAN INFO */}
        <SectionLabel>Your Plan Info</SectionLabel>
        <div className="grid grid-cols-2 gap-3.5">
          {/* My Plan Card */}
          <button
            onClick={handleExplorePlan}
            className="flex flex-col items-center bg-portal-card border border-portal-border rounded-2xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <p className="text-[15px] font-bold mb-1">My Plan</p>
            <p className="text-[12.5px] text-portal-sub leading-[1.4] mb-3">Coverage, limits &amp; benefits</p>
            <p className="text-[13px] font-bold text-portal-blue">Explore &gt;</p>
          </button>

          {/* Support Card */}
          <button
            onClick={() => setShowSupportPopup(true)}
            className="flex flex-col items-center bg-portal-orange-light border border-portal-orange-border rounded-2xl p-4 text-center hover:shadow-md transition-shadow"
          >
            <p className="text-[15px] font-bold mb-1">Support</p>
            <p className="text-[12.5px] text-portal-sub leading-[1.4] mb-3">Get help with your plan</p>
            <p className="text-[13px] font-bold text-portal-orange">Get Help &gt;</p>
          </button>
        </div>

        {reimbursementRequired ? (
          <>
            {/* HOW TO USE YOUR PLAN */}
            {kycRequired && (
              <>
                <SectionLabel>How To Use Your Plan</SectionLabel>
                {scanTile}
              </>
            )}

            {/* TWO OPTIONS TO BOOK YOUR DENTAL APPOINTMENT */}
            <SectionLabel className="mt-[26px]">Two Options To Book Your Dental Appointment</SectionLabel>
            <div className="grid grid-cols-2 gap-3.5">
              {/* Network Clinic Card */}
              <button
                onClick={handleBookAppointment}
                className="flex flex-col items-center gap-2 text-center bg-portal-green-light border border-portal-green-border rounded-2xl p-4 hover:shadow-md transition-shadow"
              >
                <p className="text-[14.5px] font-bold">ToothLens Network Clinic</p>
                <p className="text-xs text-portal-sub leading-[1.45]">Choose a ToothLens network clinic and get cashless treatment.</p>
                <span className="text-[12.5px] font-bold text-portal-green mt-0.5">Find Clinic&nbsp;&gt;</span>
              </button>

              {/* Reimbursement Card — holds several actions, so a plain tile rather than one button */}
              <div className="flex flex-col items-center gap-2 text-center bg-portal-purple-light border border-portal-purple-border rounded-2xl p-4">
                <p className="text-[14.5px] font-bold">Reimbursement</p>
                <p className="text-xs text-portal-sub leading-[1.45]">Visit any dental clinic. Pay at the clinic and get reimbursed.</p>
                <p className="text-xs text-portal-sub leading-[1.45]">
                  First upload the prescription to get your approval. After completing your treatment, upload proof of treatment with the required documents.
                </p>
                <button
                  onClick={() => setReimbursementView('request')}
                  className="text-[12.5px] font-bold text-portal-purple mt-0.5 hover:underline"
                >
                  Submit prescription&nbsp;&gt;
                </button>
                <button
                  onClick={() => setReimbursementView('proof')}
                  className="text-[12.5px] font-bold text-portal-purple mt-1 hover:underline"
                >
                  Upload proof of treatment&nbsp;&gt;
                </button>
                <button
                  onClick={() => setReimbursementView('list')}
                  className="text-xs font-semibold text-portal-sub mt-1 hover:underline"
                >
                  View status&nbsp;&gt;
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* HOW TO USE YOUR PLAN — without Reimbursement, booking is the only way to use the
                plan, so it follows the scan as step 2. */}
            <SectionLabel>How To Use Your Plan</SectionLabel>
            {kycRequired && scanTile}
            <button
              onClick={handleBookAppointment}
              className={`w-full flex items-center justify-between gap-3 text-left bg-portal-green-light border border-portal-green-border rounded-[18px] px-[18px] py-3 hover:shadow-md transition-shadow ${kycRequired ? 'mt-3.5' : ''}`}
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[15px] font-bold">Book Appointment</p>
                  {kycRequired && <StepPill step={2} className="bg-portal-green text-white" />}
                </div>
                <p className="text-[11px] text-portal-sub leading-[1.3] mt-px">Find a dentist &amp; book your appointment.</p>
              </div>
              <span className="flex-shrink-0 whitespace-nowrap bg-portal-green text-white text-xs font-bold px-[13px] py-[7px] rounded-[9px] mt-0.5">
                Book &gt;
              </span>
            </button>
          </>
        )}

        {/* MY ACTIVITY */}
        <SectionLabel className="mt-[26px]">My Activity</SectionLabel>
        <button
          onClick={() => navigate('/phonepe/bookings')}
          className="w-full flex items-center gap-3 text-left bg-white border border-portal-border rounded-2xl p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex-1">
            <p className="text-[15px] font-bold mb-0.5">My Bookings</p>
            <p className="text-[12.5px] text-portal-sub">View &amp; manage your appointments</p>
          </div>
          <ChevronRight className="h-5 w-5 text-portal-label" />
        </button>
      </div>

      {/* Support Popup */}
      {showSupportPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-xl">
            {/* Header */}
            <div className="bg-orange-500 p-5 rounded-t-2xl flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Headphones className="h-5 w-5 text-white" />
                <h2 className="text-lg font-bold text-white">Toothlens Support</h2>
              </div>
              <button
                onClick={() => setShowSupportPopup(false)}
                className="text-white hover:text-orange-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Contact Details */}
            <div className="p-6 space-y-5">
              {/* Benefits Assistance */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Benefits Assistance</p>
                <div className="space-y-2">
                  <a href="https://wa.me/919384814056" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">WhatsApp</p>
                      <p className="text-gray-800 font-semibold text-sm">93848 14056</p>
                    </div>
                  </a>
                  <a href="mailto:onboarding@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">onboarding@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Appointments */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Appointments</p>
                <div className="space-y-2">
                  <a href="https://wa.me/919384820449" target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">WhatsApp</p>
                      <p className="text-gray-800 font-semibold text-sm">93848 20449</p>
                    </div>
                  </a>
                  <a href="mailto:appointments@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">appointments@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Feedback & Concerns */}
              <div>
                <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-2">Feedback &amp; Concerns</p>
                <div className="space-y-2">
                  <a href="mailto:insurance@toothlens.com" className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <img src={emailIcon} alt="Email" className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">E-mail</p>
                      <p className="text-gray-800 font-semibold text-sm">insurance@toothlens.com</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reimbursement Section Modal — a bottom sheet on phones, a centred card from sm up. The
          section and each of its forms render the title it is labelled by (DIALOG_TITLE_ID). */}
      {reimbursementView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={DIALOG_TITLE_ID}
            className="bg-white rounded-t-[20px] sm:rounded-2xl max-w-2xl w-full max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] flex flex-col overflow-hidden"
          >
            <ReimbursementSection initialView={reimbursementView} onClose={() => setReimbursementView(null)} />
          </div>
        </div>
      )}

      {/* Loading Modal */}
      {isLoading && !showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full">
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full opacity-20 animate-pulse"></div>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading...</h3>
              <p className="text-gray-600 text-center">Please wait while we fetch your details</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorMessage && !showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white">Notice</h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-red-600 text-base">
                {errorMessage}
              </div>

              <button
                onClick={() => setErrorMessage('')}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KYC Status Modal (all-pending or mixed or blocked) */}
      {kycPendingDetails.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Plan Activation Status</h2>
                <p className="text-blue-100 text-sm mt-1">Member status overview</p>
              </div>
              <button
                onClick={() => { setKycPendingDetails([]); setKycBlockedReason(''); }}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            {kycBlockedReason ? (
              <div className="mx-6 mt-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </div>
                <p className="text-sm text-red-700 font-medium">{kycBlockedReason}</p>
              </div>
            ) : null}
            <div className="p-6 space-y-3">
              {kycPendingDetails.map((member) => {
                const statusConfig = {
                  'Active':           { bg: 'bg-green-50',  border: 'border-green-100',  badge: 'bg-green-100 text-green-700' },
                  'Under Review':     { bg: 'bg-blue-50',   border: 'border-blue-100',   badge: 'bg-blue-100 text-blue-700' },
                  'In Process':       { bg: 'bg-blue-50',   border: 'border-blue-100',   badge: 'bg-blue-100 text-blue-700' },
                  'Retake Requested': { bg: 'bg-amber-50',  border: 'border-amber-100',  badge: 'bg-amber-100 text-amber-700' },
                  'X-Ray Requested':  { bg: 'bg-amber-50',  border: 'border-amber-100',  badge: 'bg-amber-100 text-amber-700' },
                  'Rejected':         { bg: 'bg-red-50',    border: 'border-red-100',    badge: 'bg-red-100 text-red-700' },
                  'Pending':          { bg: 'bg-orange-50', border: 'border-orange-100', badge: 'bg-orange-100 text-orange-700' },
                }[member.status] || { bg: 'bg-gray-50', border: 'border-gray-100', badge: 'bg-gray-100 text-gray-700' };

                return (
                  <div
                    key={member.uid}
                    className={`flex items-center justify-between p-3 rounded-xl border ${statusConfig.bg} ${statusConfig.border}`}
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 capitalize truncate">{member.name}</p>
                      <p className="text-sm text-gray-500 capitalize">{member.relation}</p>
                      <span
                        title={member.uid}
                        className="inline-block max-w-full truncate mt-1 text-[11px] font-mono font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded"
                      >
                        {member.uid}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusConfig.badge}`}>
                        {member.status}
                      </span>
                      {member.canBook && member.patientData && (
                        <button
                          onClick={() => {
                            setKycPendingDetails([]);
                            goToBooking(member.patientData);
                          }}
                          className="text-xs font-semibold bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors"
                        >
                          Book
                        </button>
                      )}
                      {member.status === 'Pending' && (
                        <button
                          onClick={() => { setKycPendingDetails([]); handleOpenKYC(); }}
                          className="text-xs font-semibold bg-orange-500 text-white px-3 py-1 rounded-full hover:bg-orange-600 transition-colors"
                        >
                          Complete Scan
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Patient Selection Modal */}
      {showPatientSelection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div>
                <h2 className="text-xl font-bold text-white">Choose Member</h2>
                <p className="text-blue-100 text-sm mt-1">Select a member to book an appointment</p>
              </div>
              <button
                onClick={handleClosePatientSelection}
                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                disabled={isLoading}
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Member list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {patientsList.map((patient, idx) => (
                <div
                  key={patient._id || patient.uid || idx}
                  className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 transition-all"
                >
                  {/* Avatar + info */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">
                        {(patient.name || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 capitalize">{patient.name}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {patient.relation_to_patient && (
                          <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full capitalize">
                            {patient.relation_to_patient}
                          </span>
                        )}
                        {patient.phone_number && (
                          <span className="text-xs text-gray-500">{patient.phone_number}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Book button */}
                  <button
                    onClick={() => handlePatientSelect(patient)}
                    className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition-all shadow-sm hover:shadow-md"
                  >
                    <Calendar className="w-3.5 h-3.5" />
                    Book
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Session Timeout Modal */}
      {showSessionTimeout && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <h2 className="text-xl font-bold text-white">Session Timeout</h2>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-gray-700 text-base">
                Your session has expired after 2 hours for security reasons. Please login again to continue.
              </div>

              <button
                onClick={() => {
                  clearPhonepeSession();
                  navigate('/phonepe/login');
                }}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhonepeDashboard;
